import { useEffect, useRef, useState, type PointerEvent } from "react";
import { useT } from "../settings/context";
import { Button } from "../ui/Button";
import {
  MAX_ZOOM,
  MIN_ZOOM,
  applyGesture,
  centeredOffset,
  clampZoom,
  cropToFile,
  displayedSize,
  middleOf,
  sourceRect,
  spanOf,
  zoomKeepingCenter,
  type Frame,
  type GestureStart,
  type Offset,
  type Size,
} from "./crop";

/**
 * ═══ CHOISIR LA ZONE DU VISAGE, COMME AU BACK OFFICE ═══
 *
 * Un carré, la photo dedans, et deux gestes : on la fait glisser, on règle le zoom. Ce
 * qui reste dans le rond est ce qui part. La géométrie n'est pas ici — elle est dans
 * `crop.ts`, avec ses tests ; ce composant ne fait que la relier aux doigts.
 *
 * ⚠️ LE ROND N'EST PAS LE DÉCOUPAGE, c'est le rappel de ce que l'avatar montrera. Le
 * fichier envoyé est le CARRÉ entier : Rekognition y cherche un visage, et lui rogner
 * les coins ne l'aiderait pas. Le rond dit seulement « voilà ce qu'on verra de vous ».
 *
 * ⚠️ TROIS FAÇONS DE ZOOMER, PARCE QU'IL Y A TROIS MAINS. Le pincement sur un téléphone,
 * où tout le monde le fait sans qu'on l'écrive ; le curseur, qui est la seule à
 * fonctionner au clavier et à se dire à un lecteur d'écran ; les deux doigts relâchés à
 * des instants différents, qui ne doivent pas faire sauter la photo.
 */
export function Cropper({
  file,
  onCancel,
  onConfirm,
}: {
  file: File;
  onCancel: () => void;
  /** Le carré découpé, prêt à partir — ou la photo d'origine si le canevas a refusé. */
  onConfirm: (framed: File) => void;
}) {
  const t = useT();
  const image = useRef<HTMLImageElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const [view, setView] = useState(0);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [working, setWorking] = useState(false);

  /**
   * ⚠️ L'ADRESSE LOCALE SE RÉVOQUE, ET ELLE NAÎT DANS L'EFFET QUI LA RÉVOQUE. Une `blob:`
   * gardée vivante retient la photo entière en mémoire jusqu'au rechargement de la page —
   * et sur un téléphone, ce sont plusieurs mégaoctets par essai de cadrage.
   *
   * ⚠️ MAIS CRÉÉE AU RENDU, ELLE MOURAIT AVANT D'AVOIR SERVI. Fabriquée par un `useMemo`
   * et révoquée au démontage, elle ne survivait pas au montage-démontage-remontage que
   * React fait en développement : le mémo, lui, n'est pas rejoué, et le second montage
   * héritait d'une adresse déjà révoquée — le cadre restait vide, la photo introuvable
   * (`ERR_FILE_NOT_FOUND`), et on ne pouvait plus choisir la zone du visage. Née ici,
   * chaque montage a la sienne, et ne révoque que la sienne.
   */
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const made = URL.createObjectURL(file);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [file]);

  /* Le côté du carré est celui que le CSS décide (il tient la largeur du dialogue, donc
     l'écran) : on le mesure, on ne le fixe pas. */
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setView(el.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* Photo connue, cadre connu : elle se pose au milieu. Une rotation de l'appareil
     repasse par là, et repartir d'un cadrage centré vaut mieux qu'en garder un calculé
     pour une autre largeur. */
  useEffect(() => {
    if (!natural || view <= 0) return;
    setZoom(MIN_ZOOM);
    setOffset(centeredOffset(natural, view));
  }, [natural, view]);

  const frame: Frame | null =
    natural && view > 0 ? { natural, view, zoom, offset } : null;

  /**
   * Les doigts posés sur le cadre, et d'où part le geste en cours. L'arithmétique est
   * dans `crop.ts` (`applyGesture`), avec ses tests : ici, rien que les écouteurs.
   */
  const touches = useRef(new Map<number, Offset>());
  const start = useRef<GestureStart | null>(null);

  const doigts = (): Offset[] => [...touches.current.values()];
  /* Le geste REPART du cadrage courant à chaque doigt posé ou levé. */
  const reprise = () => {
    const points = doigts();
    start.current = { offset, zoom, at: middleOf(points), span: spanOf(points) };
  };

  const onDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    reprise();
  };

  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!frame || !touches.current.has(event.pointerId) || !start.current) return;
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const next = applyGesture(frame, start.current, doigts());
    setZoom(next.zoom);
    setOffset(next.offset);
  };

  const onUp = (event: PointerEvent<HTMLDivElement>) => {
    touches.current.delete(event.pointerId);
    if (touches.current.size > 0) reprise();
    else start.current = null;
  };

  /**
   * ⚠️ REFERMÉE PENDANT LE DÉCOUPAGE, RIEN NE PART. Le canevas prend un instant sur une
   * grande photo, et la croix de la fenêtre reste vivante pendant ce temps : sans ce
   * garde-fou, quitter le cadrage à cet instant envoyait quand même le portrait.
   *
   * ⚠️ ET IL SE RÉARME AU MONTAGE, SANS QUOI IL CONDAMNE LE CADRAGE. Posé à `false` par
   * le seul nettoyage, il ne redevenait jamais `true` au montage-démontage-remontage que
   * React fait en développement (`StrictMode`, cf. `main.tsx`) : la référence survit à ce
   * faux démontage, donc « Valider » découpait la photo puis la jetait en silence — la
   * fenêtre restait ouverte, et rien ne partait. Même leçon que l'adresse `blob:`
   * ci-dessus : ce qu'un nettoyage défait, le montage doit le refaire.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => void (alive.current = false);
  }, []);

  const send = async () => {
    if (!frame || !image.current) return onConfirm(file);
    setWorking(true);
    try {
      const framed = await cropToFile(image.current, sourceRect(frame), file);
      if (alive.current) onConfirm(framed);
    } finally {
      setWorking(false);
    }
  };

  const size = frame ? displayedSize(frame) : null;

  return (
    <div className="crp">
      <div
        className="crp__f"
        ref={box}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <img
          ref={image}
          className="crp__i"
          /* Tant que l'adresse n'est pas faite (le premier rendu), pas de `src` du
             tout : une chaîne vide relancerait le chargement de la page elle-même. */
          src={url ?? undefined}
          alt=""
          draggable={false}
          onLoad={(e) =>
            setNatural({
              width: e.currentTarget.naturalWidth,
              height: e.currentTarget.naturalHeight,
            })
          }
          style={
            size
              ? {
                  width: `${size.width}px`,
                  height: `${size.height}px`,
                  transform: `translate(${offset.x}px, ${offset.y}px)`,
                }
              : /* Tant qu'on ne sait pas la poser, elle ne s'affiche pas : une photo
                   rendue à sa taille d'origine remplirait l'écran un instant. */
                { visibility: "hidden" }
          }
        />
        {/* Décoratif : le message ci-dessous dit la même chose à qui ne voit pas le rond. */}
        <span className="crp__m" aria-hidden="true" />
      </div>

      <label className="crp__z">
        <span>{t("PORTRAIT_ZOOM", "Zoom")}</span>
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={(e) => {
            const next = clampZoom(Number(e.target.value));
            setZoom(next);
            if (frame) setOffset(zoomKeepingCenter(frame, next));
          }}
        />
      </label>

      <p className="crp__h">
        {t(
          "PORTRAIT_FRAME_HELP",
          "Déplacez la photo et réglez le zoom pour centrer votre visage.",
        )}
      </p>

      <div className="crp__a">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("CANCEL", "Annuler")}
        </Button>
        <Button type="button" loading={working} onClick={() => void send()}>
          {t("VALIDATE", "Valider")}
        </Button>
      </div>
    </div>
  );
}
